import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { FadeIn } from './ui/FadeIn';
import { FloatingElements } from './ui/FloatingElements';
import { ArrowRightIcon, LockIcon, SparklesIcon } from 'lucide-react';
const SHOPIFY_CART_URL = 'https://www.nextlevelvital.com/cart/47874715320564:1';
const ORIGINAL_PRICE = 19;
const CURRENT_PRICE = 6.36;
const DISCOUNT_PERCENT = 66;
export function FinalCTASection() {
  const count = useMotionValue(ORIGINAL_PRICE);
  const formatted = useTransform(count, v => v.toFixed(2));
  const [displayPrice, setDisplayPrice] = useState(ORIGINAL_PRICE.toFixed(2));
  useEffect(() => {
    const unsubscribe = formatted.on('change', latest => setDisplayPrice(latest));
    return () => unsubscribe();
  }, [formatted]);
  const handleAddToCart = () => {
    // Meta Pixel AddToCart event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Guía 7 Silencios',
        content_type: 'product',
        value: CURRENT_PRICE,
        currency: 'USD'
      });
    }
    // Redirect to Shopify cart
    window.open(SHOPIFY_CART_URL, '_blank');
  };
  return <section id="final-cta" className="py-32 px-4 md:px-8 bg-burgundy-dark relative overflow-hidden" data-id="element-25">
      <FloatingElements count={6} colors={['bg-gold', 'bg-rose', 'bg-burgundy-light']} data-id="element-26" />

      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-burgundy-light/20 rounded-full blur-3xl pointer-events-none" data-id="element-27"></div>

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-28">
        <FadeIn data-id="element-29">
          <SparklesIcon className="w-8 h-8 text-gold mx-auto mb-6 opacity-80" data-id="element-30" />
          <h2 className="text-4xl md:text-5xl font-serif text-cream-light mb-6 leading-tight" data-id="element-31">
            Tu nueva historia comienza hoy
          </h2>
          <p className="text-xl text-cream/80 max-w-2xl mx-auto mb-16 leading-relaxed" data-id="element-32">
            No dejes que pase un día más sintiéndote invisible. Mereces
            reconectarte contigo y con tu pareja.
          </p>
        </FadeIn>

        <FadeIn delay={0.2} className="relative" data-id="element-33">
          {/* Pulsing Border Glow */}
          <motion.div animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [0.98, 1.02, 0.98]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} className="absolute -inset-1 bg-gradient-to-r from-gold via-rose to-gold rounded-[3rem] blur-xl opacity-40" data-id="element-34" />

          <div className="bg-white/95 backdrop-blur-md p-12 md:p-16 rounded-[3rem] shadow-2xl relative z-10" data-id="element-35">
            {/* Discount Badge */}
            <motion.div initial={{
            scale: 0,
            rotate: -12
          }} whileInView={{
            scale: 1,
            rotate: -6
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.3
          }} className="absolute -top-5 -right-3 md:right-8 bg-burgundy text-white font-bold text-lg md:text-xl px-5 py-2.5 rounded-2xl shadow-lg z-20 border-2 border-white" data-id="element-36">
              -{DISCOUNT_PERCENT}%
            </motion.div>

            <h3 className="text-2xl text-charcoal-light mb-8 font-medium" data-id="element-37">
              Consigue la guía completa
            </h3>

            {/* Price Display */}
            <div className="flex items-center justify-center gap-4 md:gap-6 mb-4" data-id="element-38">
              {/* Original Price - Strikethrough */}
              <span className="text-3xl md:text-4xl font-serif text-charcoal-light/50 line-through decoration-burgundy/60 decoration-2" data-id="element-39">
                ${ORIGINAL_PRICE}
              </span>

              {/* Animated Current Price */}
              <motion.div onViewportEnter={() => animate(count, CURRENT_PRICE, {
              duration: 2.5,
              ease: 'easeOut'
            })} viewport={{
              once: true
            }} className="text-7xl md:text-8xl font-serif font-bold text-burgundy-dark flex items-baseline gap-1" data-id="element-40">
                <span data-id="element-41">$</span>
                <span data-id="element-42">{displayPrice}</span>
              </motion.div>

              <span className="text-2xl md:text-3xl text-charcoal-light font-sans font-normal self-end mb-3" data-id="element-43">
                USD
              </span>
            </div>

            <p className="text-charcoal-light mb-10 bg-cream inline-block px-8 py-3 rounded-full text-sm md:text-base font-medium border border-cream-dark" data-id="element-44">
              Un solo pago · Acceso de por vida · Descarga instantánea
            </p>

            {/* Big CTA Button */}
            <motion.button onClick={handleAddToCart} whileHover={{
            scale: 1.02,
            y: -3
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0px 4px 20px rgba(201,169,110,0.3)', '0px 4px 50px rgba(201,169,110,0.7)', '0px 4px 20px rgba(201,169,110,0.3)']
          }} transition={{
            boxShadow: {
              repeat: Infinity,
              duration: 3,
              ease: 'easeInOut'
            },
            scale: {
              duration: 0.3
            },
            y: {
              duration: 0.3
            }
          }} className="group relative bg-gradient-to-r from-gold to-gold-dark text-burgundy-dark font-bold py-6 px-8 md:px-14 rounded-full shadow-2xl transition-all duration-500 text-center flex flex-col items-center justify-center w-full max-w-2xl mx-auto overflow-hidden mb-8" data-id="element-45">
              {/* Shimmer */}
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 z-10" data-id="element-46"></div>
              <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500 z-0" data-id="element-47"></div>

              <span className="relative z-20 text-lg md:text-xl lg:text-2xl mb-2 flex items-center gap-3 font-bold" data-id="element-48">
                ¡SÍ, QUIERO MI GUÍA POR SOLO $6.36!
                <ArrowRightIcon className="w-6 h-6 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" data-id="element-49" />
              </span>
              <span className="relative z-20 text-xs md:text-sm font-medium opacity-80 flex items-center gap-1.5" data-id="element-50">
                <LockIcon className="w-3.5 h-3.5" data-id="element-51" /> Compra 100% segura ·
                Descarga instantánea
              </span>
            </motion.button>

            <p className="text-sm text-charcoal-light/60" data-id="element-52">
              Precio especial por tiempo limitado. Normalmente ${ORIGINAL_PRICE}{' '}
              USD.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}